import styled from '@emotion/styled';
import {Container, media, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Accordion} from 'UI/components/action/Accordion';
import {IntroHeader} from 'UI/components/layout/IntoHeader';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';
import {FaqBody} from 'UI/components/modules/Faq/FaqBody';

import {FadeInAnimation} from 'UI/animations/shared';

import type {FaqType, TextImageModuleIntro} from 'types/payload-types';

/**
 * The props for the `Faq` component, which renders a list of frequently asked questions
 * inside expandable accordions.
 * This interface defines the required FAQ data, unique identifiers for navigation
 * and accessibility, and optional introductory content. It also supports a `testId`
 * for automated testing.
 */
interface ComponentProps {
    /**
     * An array of FAQ entries, where each entry contains an ID, question,
     * answer, and optional image metadata.
     */
    faqs: FaqType['faqs'];
    /**
     * An optional flag to hide the entire block when set to true.
     * When `true`, the component renders nothing.
     *
     * @default false
     */
    hideBlock?: FaqType['hideBlock'];
    /**
     * The unique identifier for the FAQ block, used for linking and accessibility.
     * This ID is essential for in-page navigation and must be unique across the page.
     */
    id: FaqType['id'];
    /**
     * The intro section configuration, which may include an overline, headline,
     * and descriptive text. If omitted, no intro is rendered.
     */
    intro?: TextImageModuleIntro;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Faq'
     */
    testId?: string;
}

/**
 * An FAQ section component that renders a list of questions and answers in an accordion format.
 * The `Faq` component uses the `IntroHeader` to optionally display an introductory section
 * (with overline, headline, and text) above the list of FAQs. Each FAQ entry is rendered inside
 * an `Accordion`, with the question as the header and the `FaqBody` as the expandable content.
 * The `FaqBody` supports both text answers and optional images.
 * The component is wrapped in MobX's `observer` to automatically re-render when observable data changes.
 * The `testId` prop is passed to the container for easier targeting in end-to-end tests.
 *
 * @param props           The component props.
 * @param props.faqs      An array of FAQ entries, each containing an ID, question, answer, and optional image information.
 * @param props.hideBlock An optional flag to hide the entire block when set to true.
 * @param props.id        The unique identifier for the FAQ block, applied to the `IntroHeader`.
 * @param props.intro     Optional intro section containing overline, headline, and text.
 * @param props.testId    An optional identifier used for testing, applied as `data-cy`. Defaults to `'Faq'`.
 * @returns A JSX element rendering the FAQ section with accordions for each question.
 *
 * @example
 * ```tsx
 * <Faq
 *   id="faq-section"
 *   intro={{ headline: 'Frequently Asked Questions', overline: 'Help', text: faqIntro }}
 *   faqs={[
 *     { id: 'faq1', question: 'What is your return policy?', answer: returnPolicy, hasImage: false },
 *     { id: 'faq2', question: 'Do you offer support?', answer: supportInfo, hasImage: true, image: supportImage }
 *   ]}
 * />
 * ```
 */
const Faq = observer(({faqs, hideBlock = false, id, intro, testId = 'Faq'}: ComponentProps) => (hideBlock ? null : (
    <BgWrapper>
        <Container testId={testId}>
            <IntroHeader blockId={id} intro={intro} />
            <ViewportTrigger baseComponent={BaseComp}>
                {faqs.map(faq => (
                    <motion.div key={faq.id} variants={FadeInAnimation}>
                        <Accordion key={faq.id} accordionId={faq.id} head={faq.question}>
                            <FaqBody answer={faq.answer} hasImage={faq.hasImage} image={faq.image} />
                        </Accordion>
                    </motion.div>
                ))}
            </ViewportTrigger>
        </Container>
    </BgWrapper>
)));

Faq.displayName = 'Faq';

export {Faq};

const BgWrapper = styled.div`
    background-color: ${({theme}) => theme.colors.neutral50};
    margin-block-end: ${spacing(-18)};
    margin-block-start: ${spacing(-18)};
    padding-block-end: ${spacing(18)};
    padding-block-start: ${spacing(18)};

    ${media('md')} {
        margin-block-end: ${spacing(-24)};
        margin-block-start: ${spacing(-24)};
        padding-block-end: ${spacing(24)};
        padding-block-start: ${spacing(24)};
    }
`;

const BaseComp = styled.div`
    margin-inline: auto;
    max-width: 92rem;
`;